/**
 * Reading and changing every trader's capabilities — ROADMAP 9.12.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * OPTIMISTIC, WITH A ROLLBACK, AND NOT POLLED
 *
 * A toggle has to feel instant or an admin double-clicks it, and a double
 * click on a capability switch is a second PATCH that puts it back. So the
 * row is updated locally the moment it is clicked, the request goes, and a
 * failure restores exactly the previous value and says why ON THAT ROW.
 *
 * NOT POLLED, unlike the simulation screen next door. That one watches a
 * session a background process is driving, so it has to re-read. Nothing
 * changes these rows except an admin on this page — polling would reorder
 * and repaint a table somebody is clicking in, which is how a mis-toggle
 * happens.
 *
 * VALIDATED ON ARRIVAL. `api.get<T>` is a type ASSERTION and not a check: the
 * response is whatever the wire delivered, and a shape that changed under us
 * reaches `.map` and takes the screen down. This is an admin screen, so the
 * blast radius of that is small — but it is the screen somebody opens when
 * they are already trying to fix something.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  validateExecutionPolicyPatch,
  type ExecutionPolicyDto,
  type ExecutionPolicyPatch,
  type TraderPolicyDto,
} from '@apna/shared'
import { api } from '@/lib/api'

const ROLES = ['OWNER', 'TRADER', 'VIEWER']

const isPolicy = (v: unknown): v is ExecutionPolicyDto => {
  if (typeof v !== 'object' || v === null) return false
  const p = v as ExecutionPolicyDto
  return (
    typeof p.manualNetting === 'boolean' &&
    typeof p.sliceEnabled === 'boolean' &&
    typeof p.autoChaseEnabled === 'boolean' &&
    typeof p.sliceMaxQty === 'number' &&
    typeof p.autoChaseMaxMs === 'number' &&
    typeof p.limitBufferPct === 'number'
  )
}

const isTrader = (v: unknown): v is TraderPolicyDto => {
  if (typeof v !== 'object' || v === null) return false
  const t = v as TraderPolicyDto
  return (
    typeof t.userId === 'string' &&
    typeof t.email === 'string' &&
    ROLES.includes(t.role) &&
    typeof t.accounts === 'number' &&
    typeof t.openTrades === 'number' &&
    // Null is a REAL answer — no row means nobody has granted anything.
    (t.policy === null || isPolicy(t.policy))
  )
}

function tradersFrom(raw: unknown): TraderPolicyDto[] {
  if (typeof raw !== 'object' || raw === null || !('traders' in raw) || !Array.isArray(raw.traders)) {
    throw new Error('The traders list came back in a shape this build does not understand.')
  }
  return raw.traders.filter(isTrader)
}

export interface TraderPolicies {
  readonly traders: readonly TraderPolicyDto[]
  readonly loading: boolean
  /** The read failed. NOT an empty deployment. */
  readonly error: string | null
  /** `userId`s with a PATCH in flight, so their row can show it. */
  readonly saving: ReadonlySet<string>
  /** Per-row refusals, keyed by `userId`. Cleared on the next attempt. */
  readonly refusals: ReadonlyMap<string, string>
  readonly refresh: () => void
  /** Change one trader. Resolves true when the server accepted it. */
  readonly update: (userId: string, patch: ExecutionPolicyPatch) => Promise<boolean>
}

export function useTraderPolicies(enabled: boolean): TraderPolicies {
  const [traders, setTraders] = useState<readonly TraderPolicyDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState<ReadonlySet<string>>(new Set())
  const [refusals, setRefusals] = useState<ReadonlyMap<string, string>>(new Map())
  const [revision, setRevision] = useState(0)

  /*
   * The rows as they are NOW, for the rollback.
   *
   * A ref rather than the state closure: two toggles on one row in quick
   * succession would both capture the same stale snapshot, and the second
   * failure would restore a value the first had already replaced.
   */
  const latest = useRef<readonly TraderPolicyDto[]>([])
  latest.current = traders

  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()
    void (async () => {
      try {
        const raw = await api.get<unknown>('/admin/traders', { signal: controller.signal })
        if (controller.signal.aborted) return
        setTraders(tradersFrom(raw))
        setError(null)
      } catch (e) {
        if (controller.signal.aborted) return
        setError(e instanceof Error ? e.message : 'Could not read the traders list.')
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    })()
    return () => controller.abort()
  }, [enabled, revision])

  const mark = useCallback((userId: string, on: boolean) => {
    setSaving((prev) => {
      const next = new Set(prev)
      if (on) next.add(userId)
      else next.delete(userId)
      return next
    })
  }, [])

  const update = useCallback(
    async (userId: string, patch: ExecutionPolicyPatch): Promise<boolean> => {
      /*
       * REFUSED BEFORE SENDING, in the server's own words — the shared
       * validator is the same function the HTTP boundary runs. A UI that
       * promises what the server refuses is the mismatch this codebase keeps
       * removing.
       */
      const invalid = validateExecutionPolicyPatch(patch)
      if (invalid) {
        setRefusals((prev) => new Map(prev).set(userId, invalid))
        return false
      }

      const before = latest.current.find((t) => t.userId === userId)?.policy ?? null
      setRefusals((prev) => {
        const next = new Map(prev)
        next.delete(userId)
        return next
      })
      // Optimistic. An absent row becomes a row of defaults plus the patch,
      // which is exactly what the server's upsert will write.
      setTraders((prev) =>
        prev.map((t) => (t.userId === userId ? { ...t, policy: { ...BLANK, ...(t.policy ?? {}), ...patch } } : t)),
      )
      mark(userId, true)

      try {
        const res = await api.patch<{ policy: unknown }>(`/admin/traders/${userId}/policy`, patch)
        if (!isPolicy(res.policy)) throw new Error('The server did not return the saved policy.')
        // Replaced with the SERVER's row, not left on the optimistic guess:
        // `updatedBy` and `updatedAt` are its to write, and the row may carry
        // a value another admin set in the same second.
        const saved = res.policy
        setTraders((prev) => prev.map((t) => (t.userId === userId ? { ...t, policy: saved } : t)))
        return true
      } catch (e) {
        setTraders((prev) => prev.map((t) => (t.userId === userId ? { ...t, policy: before } : t)))
        setRefusals((prev) =>
          new Map(prev).set(userId, e instanceof Error ? e.message : 'The change was not saved.'),
        )
        return false
      } finally {
        mark(userId, false)
      }
    },
    [mark],
  )

  return {
    traders,
    loading,
    error,
    saving,
    refusals,
    refresh: useCallback(() => setRevision((n) => n + 1), []),
    update,
  }
}

/**
 * What a trader with NO row effectively has: everything off, nothing set.
 *
 * Used only to build the optimistic row for the first grant. It is never
 * shown as a policy — a null `policy` renders as "no row" on purpose, so
 * that "nobody turned this on" stays distinguishable from "someone turned it
 * on and then off".
 */
const BLANK: ExecutionPolicyDto = {
  manualNetting: false,
  sliceEnabled: false,
  sliceMaxQty: 0,
  sliceMaxOrders: 0,
  limitBufferPct: 0.003,
  autoChaseEnabled: false,
  autoChaseMaxMs: 0,
  updatedBy: null,
  updatedAt: null,
}
